import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { setQuestionInBox } from "../../state/slice";
import { motion, AnimatePresence } from "framer-motion";
import useScreenSize from "../../hooks/useScreenSize";
import "./enhanced-suggested-questions.scss";

/**
 * Enhanced Suggested Questions Component
 *
 * Presents categorized question suggestions that users can click to populate the question input
 */
const EnhancedSuggestedQuestions = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { isMobile } = useScreenSize();
  const [isExpanded, setIsExpanded] = useState(!isMobile); // Default to collapsed on mobile
  const dispatch = useDispatch();

  const tabCategories = isMobile
    ? [
        { id: 0, name: "Activity" },
        { id: 1, name: "Capability" },
        { id: 2, name: "Complex" },
      ]
    : [
        { id: 0, name: "Activity-based" },
        { id: 1, name: "Capability-building" },
        { id: 2, name: "Complex questions" },
      ];

  const activityQuestions = [
    "I'm having to complete a contract closeout what should I consider?",
    "What is the likely purpose of a contract for a small business?",
    "How could I try and reduce value leakage in the post-award phases of the contract lifecycle?",
    "How can I encourage innovation in a supplier?",
    "How might we persuade our suppliers to provide insight to second tier supply sources?",
    "I work in Procurement, not Contract Management. How is the Contract Management Standard, 4th edition, relevant to me and my team?",
  ];

  const capabilityQuestions = [
    "Give me a 300-word summary of the Contract Management Standard and how it was created.",
    "What is the contracting lifecycle and why is it important?",
    "What are the primary roles and responsibilities of a commercial manager in the oil & gas sector?",
    "Can I have a 300-word explanation of friction points?",
    "¿Puedo tener una explicación de 300 palabras sobre los puntos de fricción?",
    "Give me a 300-word summary of the 2023 benchmark webinar",
  ];

  const complexQuestions = [
    "We're a large corporate in the business services sector about to enter into negotiations with a small supplier in the technology and software sector for an as a service offering, what should we consider. What might be their priorities in relation to CCM?",
    "What is the cycle time for high complexity international contracts in the energy sector and how does this differ from the business services sector in a table?",
    "What steps can I take to implement the CMS in my organization? Please make the steps practical and include the benefits of each.",
  ];

  const allQuestions = [
    activityQuestions,
    capabilityQuestions,
    complexQuestions,
  ];

  const questionClicked = (value: string) => {
    console.log("Selecting suggested question:", value);
    dispatch(setQuestionInBox(value));
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Question item component with animation
  const QuestionItem = ({ question }: { question: string }) =>
    isMobile ? (
      // No animations on mobile
      <div className="question-card" onClick={() => questionClicked(question)}>
        <div className="question-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <p className="question-text">{question}</p>
      </div>
    ) : (
      // Animations for desktop
      <motion.div
        className="question-card"
        whileHover={{ scale: 1.01, boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onClick={() => questionClicked(question)}
      >
        <div className="question-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <p className="question-text">{question}</p>
      </motion.div>
    );

  return (
    <div className="suggested-questions-container">
      <div className="questions-header">
        <div className="header-left">
          <div className="bulb-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="1" x2="12" y2="4"></line>
              <path d="M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"></path>
              <path d="M12 16v4"></path>
              <path d="M8 20h8"></path>
              <line x1="1" y1="12" x2="4" y2="12"></line>
              <line x1="20" y1="12" x2="23" y2="12"></line>
              <line x1="4.22" y1="19.78" x2="6.34" y2="17.66"></line>
              <line x1="19.78" y1="19.78" x2="17.66" y2="17.66"></line>
              <line x1="4.22" y1="4.22" x2="6.34" y2="6.34"></line>
              <line x1="19.78" y1="4.22" x2="17.66" y2="6.34"></line>
            </svg>
          </div>
          <span className="header-title">Suggested Questions</span>
        </div>
        <button className="toggle-button"  onClick={toggleExpanded}>
          {isExpanded ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="18 15 12 9 6 15"></polyline>
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          )}
        </button>
      </div>

      {isExpanded &&
        (isMobile ? (
          // No animations on mobile
          <div className="questions-content">
            <div className="tabs-container">
              {tabCategories.map((tab) => (
                <button
                  key={tab.id}
                  data-testid={`tab${tab.id}`}
                  className={`tab-button ${
                    activeTab === tab.id ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.name}
                </button>
              ))}
            </div>

            <div className="questions-grid">
              {allQuestions[activeTab].map((question, index) => (
                <QuestionItem
                  key={`question-${activeTab}-${index}`}
                  question={question}
                />
              ))}
            </div>
          </div>
        ) : (
          // Animations for desktop
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="questions-content"
            >
              <div className="tabs-container">
                {tabCategories.map((tab) => (
                  <button
                    key={tab.id}
                     data-testid={`tab${tab.id}`}
                    className={`tab-button ${
                      activeTab === tab.id ? "active" : ""
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.name}
                  </button>
                ))}
              </div>

              <div className="questions-grid">
                {allQuestions[activeTab].map((question, index) => (
                  <QuestionItem
                    key={`question-${activeTab}-${index}`}
                    question={question}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        ))}
    </div>
  );
};

export default EnhancedSuggestedQuestions;
